/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import {Input,Select} from '../components/common/FormField';
import Spinner from '../components/common/Spinner';
import api from '../services/api';
import {useTranslation} from 'react-i18next';

const BASE = process.env.REACT_APP_API_URL;

async function fetchTerm(path:string){
  const r=await fetch(`${BASE}${path}`,{headers:{Authorization:`Bearer ${localStorage.getItem('accessToken')}`}});
  return r.json();
}

export default function TerminologyPage(){
  const {t}=useTranslation();
  const [tab,setTab]=useState<'icd11'|'snomed'|'loinc'|'mapping'|'dha'>('icd11');
  const [query,setQuery]=useState('');
  const [results,setResults]=useState<any[]>([]);
  const [loading,setLoading]=useState(false);
  const [selected,setSelected]=useState<any>(null);
  const [stats,setStats]=useState<any>({});
  const [commonLab,setCommonLab]=useState<any[]>([]);
  const [commonSNOMED,setCommonSNOMED]=useState<any>({});
  const [vitalLOINC,setVitalLOINC]=useState<any[]>([]);
  const [dhaHits,setDhaHits]=useState<any[]>([]);
  const [dhaMaps,setDhaMaps]=useState<any[]>([]);
  const [dhaCfg,setDhaCfg]=useState<any>({});
  const [dhaCatalog,setDhaCatalog]=useState<'ge'|'inv'|'ac'>('ge');
  const [mapFilter,setMapFilter]=useState('');
  const [mapForm,setMapForm]=useState({localSystem:'drug',localCode:'',localDisplay:'',targetSystem:'',targetCode:'',targetDisplay:'',terminologyType:'MEDICATION'});

  useEffect(()=>{
    fetchTerm('/terminology/stats').then(r=>setStats(r.data||r||{})).catch(()=>{});
    fetchTerm('/terminology/loinc/lab-common').then(r=>setCommonLab(r.data||r||[])).catch(()=>{});
    fetchTerm('/terminology/snomed/common').then(r=>setCommonSNOMED(r.data||r||{})).catch(()=>{});
    fetchTerm('/terminology/loinc/vitals').then(r=>setVitalLOINC(r.data||r||[])).catch(()=>{});
    fetchTerm('/terminology/dha/config').then(r=>setDhaCfg(r.data||r||{})).catch(()=>{});
    fetchTerm('/terminology/dha/mappings').then(r=>setDhaMaps(Array.isArray(r)?r:(r.data||[]))).catch(()=>{});
  },[]);

  const search=useCallback(async()=>{
    if(tab==='dha'){
      setLoading(true);
      try{
        const path=`/terminology/dha/catalog?catalog=${dhaCatalog}&q=${encodeURIComponent(query||'')}&count=40`;
        const r=await fetchTerm(path);
        setDhaHits(r.contains||r.data?.contains||[]);
      }catch{setDhaHits([]);}
      finally{setLoading(false);}
      return;
    }
    if(!query||query.length<2){setResults([]);return;}
    setLoading(true);
    try{
      const path=tab==='icd11'?`/terminology/icd11/search?q=${encodeURIComponent(query)}&limit=20`:
                 tab==='snomed'?`/terminology/snomed/search?q=${encodeURIComponent(query)}&limit=20`:
                 `/terminology/loinc/search?q=${encodeURIComponent(query)}&limit=20`;
      const r=await fetchTerm(path);
      setResults(r.data||r||[]);
    }catch{setResults([]);}
    finally{setLoading(false);}
  },[query,tab,dhaCatalog]);

  useEffect(()=>{const t=setTimeout(search,350);return()=>clearTimeout(t);},[search]);

  const TABS=[['icd11','🏥 ICD-11'],['snomed','🧬 SNOMED CT'],['loinc','🔬 LOINC'],['dha','🇰🇪 DHA Masters'],['mapping','🗺️ Mapping']];
  const catalogMeta:Record<string,{label:string;type:string;systemHint:string}>={
    ge:{label:'GE Generic Products',type:'MEDICATION',systemHint:'generic-products-cs'},
    inv:{label:'MOH Investigations (INV)',type:'SERVICE_REQUEST',systemHint:'investigations-cs'},
    ac:{label:'AC Active Components',type:'ALLERGY',systemHint:'active-components-cs'},
  };
  const filteredMaps=dhaMaps.filter((m:any)=>{
    if(!mapFilter) return true;
    const q=mapFilter.toLowerCase();
    return [m.localCode,m.localDisplay,m.targetCode,m.targetDisplay,m.terminologyType,m.localSystem].join(' ').toLowerCase().includes(q);
  });

  return(
    <Layout title="Clinical Terminology">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-5">
        <div className="card p-3 text-center"><p className="text-xl font-bold text-[#02357A]">{stats.icd11Count?.toLocaleString()||'—'}</p><p className="text-xs text-slate-500 mt-0.5">ICD-11</p></div>
        <div className="card p-3 text-center"><p className="text-xl font-bold text-green-600">{stats.snomedCount?.toLocaleString()||'—'}</p><p className="text-xs text-slate-500 mt-0.5">SNOMED</p></div>
        <div className="card p-3 text-center"><p className="text-xl font-bold text-purple-600">{stats.loincCount?.toLocaleString()||'—'}</p><p className="text-xs text-slate-500 mt-0.5">LOINC</p></div>
        <div className="card p-3 text-center"><p className="text-xl font-bold text-teal-700">{stats.genericProductsCount?.toLocaleString()||'—'}</p><p className="text-xs text-slate-500 mt-0.5">GE Products</p></div>
        <div className="card p-3 text-center"><p className="text-xl font-bold text-sky-700">{stats.mohInvestigationsCount?.toLocaleString()||'—'}</p><p className="text-xs text-slate-500 mt-0.5">MOH INV</p></div>
        <div className="card p-3 text-center"><p className="text-xl font-bold text-amber-700">{stats.activeComponentsCount?.toLocaleString()||'—'}</p><p className="text-xs text-slate-500 mt-0.5">AC Allergens</p></div>
        <div className="card p-3 text-center"><p className="text-xl font-bold text-slate-700">{stats.mappingCount?.toLocaleString()||'—'}</p><p className="text-xs text-slate-500 mt-0.5">Mappings</p></div>
      </div>

      <div className="flex gap-2 mb-5">
        {TABS.map(([t,l])=>(
          <button key={t} onClick={()=>{setTab(t as any);setQuery('');setResults([]);setSelected(null);}}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${tab===t?'bg-[#02357A] text-white shadow':'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{l}</button>
        ))}
      </div>

      {tab!=='mapping'&&tab!=='dha'&&(
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Search Panel */}
          <div className="lg:col-span-2 space-y-3">
            <div className="card p-4">
              <label className="label text-xs">Search {tab==='icd11'?'ICD-11':tab==='snomed'?'SNOMED CT':'LOINC'}</label>
              <Input value={query} onChange={e=>setQuery(e.target.value)} placeholder={tab==='icd11'?'Search by code or description...':tab==='snomed'?'Search by term or concept ID...':'Search by name or LOINC number...'}/>
              {query.length>0&&query.length<2&&<p className="text-xs text-slate-400 mt-1">Type at least 2 characters</p>}
            </div>

            {loading?<div className="card p-4"><Spinner size="6"/></div>:results.length>0&&(
              <div className="card overflow-hidden max-h-[500px] overflow-y-auto">
                {results.map((r:any,i)=>(
                  <div key={i} onClick={()=>setSelected(r)}
                    className={`p-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-all ${selected===r?'bg-blue-50':''}`}>
                    {tab==='icd11'&&<>
                      <p className="font-mono text-xs font-bold text-[#02357A]">{r.code}</p>
                      <p className="text-sm text-slate-700 mt-0.5 leading-tight">{r.description}</p>
                      {r.type&&<p className="text-xs text-slate-400 mt-0.5 capitalize">{r.type} code</p>}
                    </>}
                    {tab==='snomed'&&<>
                      <p className="font-mono text-xs font-bold text-green-600">{r.conceptId}</p>
                      <p className="text-sm text-slate-700 mt-0.5">{r.preferredTerm}</p>
                      <div className="flex gap-1 mt-1">
                        {r.semanticTag&&<span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded">{r.semanticTag}</span>}
                        {r.icd10Map&&<span className="text-xs bg-blue-50 text-[#02357A] px-1.5 py-0.5 rounded">ICD: {r.icd10Map}</span>}
                      </div>
                    </>}
                    {tab==='loinc'&&<>
                      <p className="font-mono text-xs font-bold text-purple-600">{r.loincNum}</p>
                      <p className="text-sm text-slate-700 mt-0.5">{r.longCommonName}</p>
                      <div className="flex gap-1 mt-1">
                        {r.loincClass&&<span className="text-xs bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded">{r.loincClass}</span>}
                        {r.exampleUnits&&<span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{r.exampleUnits}</span>}
                      </div>
                    </>}
                  </div>
                ))}
              </div>
            )}
            {query.length>=2&&!loading&&results.length===0&&(
              <div className="card p-6 text-center"><p className="text-slate-500 text-sm">No results found for "{query}"</p></div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-3">
            {selected?(
              <div className="card p-5">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className={`font-mono text-lg font-black ${tab==='icd11'?'text-[#02357A]':tab==='snomed'?'text-green-600':'text-purple-600'}`}>
                      {tab==='icd11'?selected.code:tab==='snomed'?selected.conceptId:selected.loincNum}
                    </span>
                    <p className="text-slate-500 text-xs mt-0.5">{tab==='icd11'?'ICD-11 (WHO MMS) Code':tab==='snomed'?'SNOMED CT Concept':'LOINC Code'}</p>
                  </div>
                  <button onClick={()=>navigator.clipboard.writeText(tab==='icd11'?selected.code:tab==='snomed'?selected.conceptId:selected.loincNum)} className="btn-secondary text-xs py-1 px-3">📋 Copy</button>
                </div>

                {tab==='icd11'&&(
                  <div className="space-y-3">
                    <div><p className="text-xs text-slate-400 uppercase tracking-wide">{t('common.description','Description')}</p><p className="font-semibold text-slate-800 mt-0.5">{selected.description}</p></div>
                    {selected.definition&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Definition</p><p className="text-slate-700 text-sm mt-0.5">{selected.definition}</p></div>}
                    {selected.chapter&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Chapter</p><p className="text-slate-700 mt-0.5"><span className="font-mono font-bold">{selected.chapter}</span></p></div>}
                    {selected.type&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Code Type</p><p className="text-slate-700 mt-0.5 capitalize">{selected.type}</p></div>}
                    <p className="text-xs text-slate-400">Source: WHO ICD-11 MMS, via NLM Clinical Table Search Service</p>
                  </div>
                )}

                {tab==='snomed'&&(
                  <div className="space-y-3">
                    <div><p className="text-xs text-slate-400 uppercase tracking-wide">Preferred Term</p><p className="font-semibold text-slate-800 mt-0.5">{selected.preferredTerm}</p></div>
                    {selected.fullySpecifiedName&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Fully Specified Name</p><p className="text-slate-700 mt-0.5">{selected.fullySpecifiedName}</p></div>}
                    {selected.hierarchy&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Hierarchy</p><span className="inline-block mt-0.5 bg-green-50 text-green-700 text-xs px-2 py-0.5 rounded font-medium">{selected.hierarchy}</span></div>}
                    {selected.semanticTag&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Semantic Tag</p><span className="inline-block mt-0.5 bg-blue-50 text-[#02357A] text-xs px-2 py-0.5 rounded font-medium">{selected.semanticTag}</span></div>}
                    {selected.icd11Map&&<div className="bg-blue-50 p-3 rounded-lg"><p className="text-xs text-[#02357A] font-semibold">ICD-11 Mapping</p><span className="font-mono font-bold text-[#02357A]">{selected.icd11Map}</span></div>}
                    {selected.synonyms&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Synonyms</p><p className="text-slate-600 text-sm mt-0.5">{selected.synonyms}</p></div>}
                  </div>
                )}

                {tab==='loinc'&&(
                  <div className="space-y-3">
                    <div><p className="text-xs text-slate-400 uppercase tracking-wide">Long Common Name</p><p className="font-semibold text-slate-800 mt-0.5">{selected.longCommonName}</p></div>
                    {selected.shortName&&<div><p className="text-xs text-slate-400 uppercase tracking-wide">Short Name</p><p className="text-slate-700 mt-0.5">{selected.shortName}</p></div>}
                    <div className="grid grid-cols-2 gap-3">
                      {[['Component',selected.component],['Property',selected.property],['Time Aspect',selected.timeAspect],['System',selected.system],['Scale Type',selected.scaleType],['Method Type',selected.methodType],['LOINC Class',selected.loincClass],['Units',selected.exampleUnits]].map(([l,v])=>v&&(
                        <div key={l}><p className="text-xs text-slate-400 uppercase tracking-wide">{l}</p><p className="text-slate-700 text-sm mt-0.5 font-medium">{v}</p></div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-5 pt-4 border-t">
                  <button className="btn-primary text-sm" onClick={()=>navigator.clipboard.writeText(JSON.stringify(selected,null,2))}>📤 Copy as JSON</button>
                </div>
              </div>
            ):(
              <div className="space-y-4">
                {tab==='icd11'&&(
                  <div className="card p-5">
                    <h3 className="font-semibold text-slate-700 mb-3">Common ICD-11 Codes</h3>
                    <div className="space-y-1.5">
                      {[['BA00','Essential hypertension'],['5A11','Type 2 diabetes mellitus'],['CA07.0','Acute upper respiratory infection'],['DA22','Gastro-oesophageal reflux disease'],['ME84.2','Low back pain'],['MG26','Fever, unknown origin'],['GC08','UTI, site not specified'],['CA40.Z','Pneumonia, organism unspecified']].map(([code,desc])=>(
                        <div key={code} onClick={()=>{setQuery(code);}} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                          <span className="font-mono text-xs font-bold text-[#02357A] w-16">{code}</span>
                          <span className="text-sm text-slate-600">{desc}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {tab==='snomed'&&(
                  <div className="card p-5">
                    <h3 className="font-semibold text-slate-700 mb-3">Common Clinical Findings</h3>
                    <div className="space-y-1.5">
                      {(commonSNOMED.findings||[]).slice(0,8).map((s:any)=>(
                        <div key={s.conceptId} onClick={()=>setQuery(s.conceptId)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                          <span className="font-mono text-xs font-bold text-green-600 w-24">{s.conceptId}</span>
                          <span className="text-sm text-slate-600">{s.preferredTerm}</span>
                          {s.icd11Map&&<span className="ml-auto text-xs bg-blue-50 text-[#02357A] px-1.5 py-0.5 rounded">{s.icd11Map}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {tab==='loinc'&&(
                  <div className="space-y-4">
                    <div className="card p-5">
                      <h3 className="font-semibold text-slate-700 mb-3">Common Lab Tests</h3>
                      <div className="space-y-1.5">
                        {commonLab.slice(0,8).map((l:any)=>(
                          <div key={l.loincNum} onClick={()=>setQuery(l.loincNum)} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                            <span className="font-mono text-xs font-bold text-purple-600 w-20">{l.loincNum}</span>
                            <span className="text-sm text-slate-600">{l.test}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="card p-5">
                      <h3 className="font-semibold text-slate-700 mb-3">Vital Sign LOINC Codes</h3>
                      <div className="space-y-1.5">
                        {vitalLOINC.map((v:any)=>(
                          <div key={v.loincNum} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                            <span className="font-mono text-xs font-bold text-purple-600 w-20">{v.loincNum}</span>
                            <span className="text-sm text-slate-600 flex-1">{v.vital}</span>
                            <span className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{v.unit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {tab==='dha'&&(
        <div className="space-y-4">
          <div className="card p-4 text-xs text-slate-600 space-y-1">
            <p>Local DHA masters (loaded in DB) for SHR coding. Edit mappings so Pharmacy / CPOE / allergies emit GE*, INV*, AC* codes.</p>
            <p className="font-mono text-[11px] text-slate-500">
              GE: {dhaCfg.genericProductsCodeSystem || 'generic-products-cs'} · INV: {dhaCfg.investigationsCodeSystem || 'investigations-cs'} · AC: {dhaCfg.activeComponentsCodeSystem || 'active-components-cs'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(['ge','inv','ac'] as const).map(c=>(
              <button key={c} type="button" onClick={()=>{setDhaCatalog(c);setQuery('');setMapForm(f=>({...f,terminologyType:catalogMeta[c].type,localSystem:c==='ge'?'drug':c==='inv'?'radiology':'allergy',targetSystem:''}));}}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${dhaCatalog===c?'bg-teal-700 text-white border-teal-700':'bg-white text-slate-600 border-slate-200'}`}>
                {catalogMeta[c].label}
                <span className="ml-1 opacity-80">({c==='ge'?stats.genericProductsCount:c==='inv'?stats.mohInvestigationsCount:stats.activeComponentsCount})</span>
              </button>
            ))}
          </div>
          <div className="card p-4">
            <label className="label text-xs">Browse / search {catalogMeta[dhaCatalog].label}</label>
            <Input value={query} onChange={e=>setQuery(e.target.value)} placeholder={dhaCatalog==='ge'?'e.g. metformin / GE00000…':dhaCatalog==='inv'?'e.g. X-Ray Chest / INV-…':'e.g. penicillin / AC…'}/>
          </div>
          {loading && <Spinner size="6"/>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[360px] overflow-y-auto">
            {dhaHits.map((c:any)=>(
              <div key={`${c.system}-${c.code}`} className="card p-3 flex justify-between gap-3">
                <div>
                  <p className="font-mono text-xs font-bold text-[#02357A]">{c.code}</p>
                  <p className="text-sm">{c.display}</p>
                  <p className="text-[11px] text-slate-400 truncate max-w-[280px]">{c.system}</p>
                </div>
                <button className="btn-secondary text-xs shrink-0" onClick={()=>setMapForm(f=>({
                  ...f,
                  targetSystem:c.system,
                  targetCode:c.code,
                  targetDisplay:c.display,
                  terminologyType:catalogMeta[dhaCatalog].type,
                }))}>Use</button>
              </div>
            ))}
            {!loading && dhaHits.length===0 && <p className="text-xs text-slate-400 col-span-2 p-2">No codes — import packs from backend/data/terminology if masters are empty.</p>}
          </div>
          <div className="card p-4 space-y-2">
            <h3 className="font-semibold text-sm">Map LOCAL → DHA code (administrators)</h3>
            <p className="text-[11px] text-slate-500">Pharmacy drugs use localSystem=<code>drug</code> + drug UUID / drugCode. Radiology/CPOE use <code>radiology</code> + order name. Allergies use <code>allergy</code>.</p>
            <div className="grid grid-cols-2 gap-2">
              <Input value={mapForm.localSystem} onChange={e=>setMapForm({...mapForm,localSystem:e.target.value})} placeholder="local system e.g. drug"/>
              <Input value={mapForm.localCode} onChange={e=>setMapForm({...mapForm,localCode:e.target.value})} placeholder="local code / UUID / name"/>
              <Input value={mapForm.localDisplay} onChange={e=>setMapForm({...mapForm,localDisplay:e.target.value})} placeholder="local display"/>
              <Input value={mapForm.terminologyType} onChange={e=>setMapForm({...mapForm,terminologyType:e.target.value})} placeholder="MEDICATION / SERVICE_REQUEST / ALLERGY"/>
              <Input value={mapForm.targetCode} onChange={e=>setMapForm({...mapForm,targetCode:e.target.value})} placeholder="DHA code"/>
              <Input value={mapForm.targetDisplay} onChange={e=>setMapForm({...mapForm,targetDisplay:e.target.value})} placeholder="DHA display"/>
            </div>
            <button className="btn-primary text-sm" onClick={async()=>{
              const body={...mapForm,targetSystem:mapForm.targetSystem||(dhaHits[0]?.system||'')};
              const r=await fetch(`${BASE}/terminology/dha/mappings`,{method:'POST',headers:{Authorization:`Bearer ${localStorage.getItem('accessToken')}`,'Content-Type':'application/json'},body:JSON.stringify(body)});
              if(!r.ok){alert('Mapping save failed — administrators only');return;}
              fetchTerm('/terminology/dha/mappings').then(x=>setDhaMaps(Array.isArray(x)?x:(x.data||[])));
              alert('Mapping saved');
            }}>Save mapping</button>
          </div>
          <div className="card overflow-hidden">
            <div className="p-3 border-b flex flex-wrap gap-2 items-center justify-between">
              <h3 className="font-semibold text-sm">Active local → DHA mappings ({filteredMaps.length})</h3>
              <Input value={mapFilter} onChange={e=>setMapFilter(e.target.value)} placeholder="Filter mappings…" className="max-w-xs"/>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50"><tr>
                <th className="p-2 text-left">Local</th><th className="p-2 text-left">DHA code</th><th className="p-2 text-left">Display</th><th className="p-2">Type</th><th className="p-2">Status</th><th className="p-2">Actions</th>
              </tr></thead>
              <tbody>
                {filteredMaps.slice(0,200).map((m:any)=>(
                  <tr key={m.id} className="border-t">
                    <td className="p-2 font-mono">{m.localSystem}/{m.localCode}</td>
                    <td className="p-2 font-mono">{m.targetCode}</td>
                    <td className="p-2">{m.targetDisplay}</td>
                    <td className="p-2">{m.terminologyType}</td>
                    <td className="p-2">{m.status}</td>
                    <td className="p-2 whitespace-nowrap">
                      <button className="text-[#02357A] mr-2" onClick={()=>setMapForm({
                        localSystem:m.localSystem||'',localCode:m.localCode||'',localDisplay:m.localDisplay||'',
                        targetSystem:m.targetSystem||'',targetCode:m.targetCode||'',targetDisplay:m.targetDisplay||'',
                        terminologyType:m.terminologyType||'MEDICATION',
                      })}>Edit</button>
                      <button className="text-red-600" onClick={async()=>{await fetch(`${BASE}/terminology/dha/mappings/${m.id}/deactivate`,{method:'PUT',headers:{Authorization:`Bearer ${localStorage.getItem('accessToken')}`}});fetchTerm('/terminology/dha/mappings').then(x=>setDhaMaps(Array.isArray(x)?x:(x.data||[])));}}>Deactivate</button>
                    </td>
                  </tr>
                ))}
                {filteredMaps.length===0&&<tr><td colSpan={6} className="p-4 text-center text-slate-400">No mappings yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab==='mapping'&&(
        <div className="space-y-5">
          <div className="card p-5">
            <h3 className="font-bold text-slate-800 mb-4">🗺️ Terminology Cross-Mapping</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
              {[['ICD-11 → SNOMED','Map diagnosis codes to SNOMED concepts','BA00 → Hypertension (38341003)','blue'],['SNOMED → ICD-11','Map clinical findings to billing codes','Fever (386661006) → MG26','green'],['LOINC → HL7 FHIR','Map lab tests to FHIR Observation codes','CBC (58410-2) → Bundle.Observation','purple']].map(([t,d,e,c])=>(
                <div key={t as string} className={`p-4 rounded-xl bg-${c}-50 border border-${c}-100`}>
                  <h4 className={`font-semibold text-${c}-700 mb-1 text-sm`}>{t}</h4>
                  <p className={`text-${c}-600 text-xs mb-2`}>{d}</p>
                  <p className={`font-mono text-xs text-${c}-800 bg-${c}-100 px-2 py-1 rounded`}>{e}</p>
                </div>
              ))}
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="font-semibold text-slate-700 mb-3">ICD-11 to SNOMED Cross Reference</h4>
              <div className="space-y-2">
                {[['BA00','Essential hypertension','38341003','Hypertensive disorder'],['5A11','Type 2 diabetes mellitus','44054006','Diabetes mellitus type 2'],['CA23.32','Asthma, unspecified','195967001','Asthma'],['GB61.Z','Chronic kidney disease, stage unspecified','431855005','CKD stage unspecified'],['BA41.Z','Acute MI, unspecified','22298006','Myocardial infarction']].map(([icd,idesc,snomed,sdesc])=>(
                  <div key={icd as string} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-200">
                    <div className="flex-1"><span className="font-mono text-xs font-bold text-[#02357A]">{icd}</span><span className="text-xs text-slate-500 ml-2">{idesc}</span></div>
                    <span className="text-slate-400">→</span>
                    <div className="flex-1"><span className="font-mono text-xs font-bold text-green-600">{snomed}</span><span className="text-xs text-slate-500 ml-2">{sdesc}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="card p-5">
            <h3 className="font-bold text-slate-800 mb-4">📊 Terminology in FHIR R4 Resources</h3>
            <div className="space-y-3">
              {[
                {resource:'Condition',system:'ICD-11 / SNOMED CT',example:'code: {system: "http://id.who.int/icd/release/11/mms", code: "BA00", display: "Essential hypertension"}'},
                {resource:'Observation (Vitals)',system:'LOINC',example:'code: {system: "http://loinc.org", code: "8310-5", display: "Body temperature"}'},
                {resource:'MedicationRequest',system:'RxNorm / SNOMED',example:'medicationCodeableConcept: {system: "http://www.nlm.nih.gov/research/umls/rxnorm", code: "723"}'},
                {resource:'DiagnosticReport',system:'LOINC',example:'code: {system: "http://loinc.org", code: "11502-2", display: "Laboratory report"}'},
                {resource:'Procedure',system:'SNOMED CT',example:'code: {system: "http://snomed.info/sct", code: "80146002", display: "Appendectomy"}'},
              ].map(r=>(
                <div key={r.resource} className="bg-slate-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-1.5"><span className="text-xs font-bold text-[#02357A] bg-blue-100 px-2 py-0.5 rounded">{r.resource}</span><span className="text-xs text-slate-500">uses {r.system}</span></div>
                  <pre className="text-xs text-slate-600 font-mono bg-white p-2 rounded border border-slate-200 overflow-x-auto">{r.example}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
